/**
 * ONE authored plan at plan level — its pass vocabulary, the single timeline every
 * subject in it shares, and which subjects it covers.
 *
 * Not any one student's own plan. A student's dates say "when I intend to
 * finish"; this says "when the author does", and the two must never be the same
 * control — adjusting your own date would otherwise silently reschedule thirty
 * people.
 *
 * **Takes the plan it edits as a prop.** It used to fetch "the" plan for a class,
 * which is the assumption the shared-plans work removed: an owner may hold
 * several named plans, so choosing between them is `PlansPanel`'s job and this
 * component is handed the winner. That is also why there is no "no plan yet"
 * path left here — creating one is now an explicit, named act.
 *
 * **The chapter grid is not here.** It moved to `PlanChapterSchedule`, one screen
 * per subject: thirty rows under this made the grid most of the page and pushed
 * everything that decides what the plan IS out of sight. Each subject row links to
 * it. Chapters exist per subject anyway — physics chapter 1 is not maths chapter 1
 * — so there was never one grid to show.
 *
 * **One cell per save**, deliberately. The history entry is then exactly what
 * changed, which is what every adopter's "the plan you took has changed" prompt
 * renders. A bulk write would collapse into one unreadable "the plan changed"
 * and the prompt would have nothing to show.
 *
 * Shared by the student app and the admin console, on the same routes. An admin
 * is the repair path for a plan the class cannot fix itself — a date nobody
 * will correct, a class whose members have all left — and with student writes
 * gated off, the console is the ONLY surface that can set one at all.
 */
import { useState } from 'react';
import { DEFAULT_STUDY_PASSES } from '@padcanvas/shared';
import { AttributeGrid, Attribute } from '../components/AttributeGrid';
import { DatePicker } from '../components/DatePicker';
import { formatPlanDate, NO_DATE } from './planDates';
import type { StudyPlan, StudyPlanPass, StudyPlanApi, StudyPlanVisibility } from './types';
import './class-shared.css';

/** What a class plans by default, from the one shared definition — the ids are
 *  load-bearing, since a class plan merges into a student's plan only because
 *  both sides spell them the same way. Copied into the plan on first write and
 *  never synced afterwards, so renaming one later does not rewrite what
 *  students already call it. */
const STARTING_PASSES: StudyPlanPass[] =
  DEFAULT_STUDY_PASSES.map(p => ({ id: p.id, name: p.label, order: p.order }));

/** How a plan's reach is named, wherever it is shown — the editor's field and
 *  the list's column read from this one map so they cannot drift apart. */
export const VISIBILITY_LABEL: Record<string, string> = {
  private: 'Only you',
  class: 'Your class',
  listed: 'Anyone',
};

/** What each state actually means for the reader picking it — the label alone
 *  ("Anyone") says who, not what follows from it. */
const VISIBILITY_HINT: Record<string, string> = {
  private: 'Nobody else can see it.',
  class: 'Members of this class can find and take it.',
  listed: 'Anyone on a matching syllabus can find and take it.',
};

/**
 * The states this plan can actually be in.
 *
 * `class` is only meaningful while a class owns the plan — on anything else it
 * names a room that does not exist, and would be shown to nobody. Everything can
 * be private, and everything can be listed: a class plan good enough to publish
 * is exactly the case this exists for.
 */
function visibilityOptions(ownerKind: string): StudyPlanVisibility[] {
  return ownerKind === 'schoolClass'
    ? ['private', 'class', 'listed']
    : ['private', 'listed'];
}

const newPassId = () =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? `p_${crypto.randomUUID().slice(0, 8)}`
    : `p_${Math.random().toString(36).slice(2, 10)}`;

export interface PlanDraft {
  name: string;
  description: string;
  subjects: string[];
  /** What the plan is written FOR. Editable only while it holds no subjects —
   *  see the Written-for block below for why. */
  board?: string;
  grade?: string;
  /** Whether this is THE plan for that level. Part of the draft rather than
   *  applied on click, unlike visibility: it takes the default AWAY from
   *  another plan, so it belongs behind the same Save as everything else. */
  isDefault?: boolean;
}

export function PlanEditor({
  api, plan, onPlanChange, subjectName, availableSubjects = [],
  onSetVisibility, canPublish = true, canEdit = true, editing = false, draft, onDraftChange,
  boardGradeOptions, canSetDefault = false,
}: {
  api: StudyPlanApi;
  /** The plan being edited. Chosen by `PlansPanel`, never resolved here. */
  plan: StudyPlan;
  /** The saved plan, so the owner of the selection can keep its copy current. */
  onPlanChange?: (plan: StudyPlan) => void;
  /** A subject's internal name is a key, never a label. */
  subjectName?: (subject: string) => string;
  /**
   * Subjects that could be added, each with the syllabus it would be pinned to.
   *
   * Replaces the old `classSubjects`, which existed because a plan covered ONE
   * subject and a pass date therefore had to ASK how far it reached — this
   * subject, or all of them. A plan now holds its subjects and shares one
   * timeline across them, so the question has a single answer and is not worth
   * asking; this prop is about what the plan COVERS instead.
   */
  availableSubjects?: Array<{ subject: string; curriculumId?: string }>;
  /** Publish / unpublish. Owned by the panel because it changes the LIST too. */
  onSetVisibility?: (visibility: 'private' | 'class' | 'listed') => void;
  /**
   * The (board, grade) pairs a plan may be written for — each board with the
   * grades that actually have a syllabus.
   *
   * Given = the Written-for block becomes editable. Absent = it stays a label,
   * which is right for a student's own surface: their plan is for their own
   * level and there is nothing to choose.
   */
  boardGradeOptions?: Array<{ board: string; grades: string[] }>;
  /**
   * Whether this surface may say which plan a level's students start on.
   *
   * The admin console alone. A student editing their own plan has no business
   * being offered a switch that would change what strangers are started on, and
   * the server refuses them anyway — this keeps the control off a screen where
   * it could only ever be refused.
   */
  canSetDefault?: boolean;
  canPublish?: boolean;
  canEdit?: boolean;
  /**
   * Whether the whole page is in edit mode.
   *
   * Owned by the panel rather than here, because ONE toggle governs both this
   * block and the chapter schedule below it — two independent edit states on one
   * page would let a reader be editing dates while the fields above them are not.
   */
  editing?: boolean;
  draft?: PlanDraft | null;
  onDraftChange?: (draft: PlanDraft) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** A default is the PLATFORM's answer for a level, and it is only applied to
   *  students who can find the plan — so it needs an owner, a level, and a
   *  publication. The server enforces all three; this is what stops the reader
   *  ticking a box that would be refused. */
  const defaultable = plan.ownerKind === 'platform'
    && !!(draft?.board ?? plan.board) && !!(draft?.grade ?? plan.grade)
    && plan.visibility === 'listed';
  /** Re-aiming a plan that holds subjects re-pins each of them and drops its
   *  chapter dates — said here, as the pair is picked, rather than only in the
   *  confirmation that follows Save. */
  const relevelling = !!draft && (plan.subjects ?? []).length > 0
    && (draft.board !== plan.board || draft.grade !== plan.grade);
  /** Why a subject chip refused to be taken. Cleared by the next good pick. */
  const [subjectError, setSubjectError] = useState<string | null>(null);

  // A plan created through the picker may still have an empty vocabulary; the
  // defaults are offered until the first edit writes them.
  /** The last date anywhere in the plan — its horizon, read off the dates rather
   *  than stored beside them. */
  const lastDate = [
    ...Object.values(plan.passDates ?? {}),
    ...(plan.subjects ?? []).flatMap(sub =>
      Object.values(sub.chapterTargets ?? {}).flatMap(t => t.map(x => x.targetDate))),
  ].filter(Boolean).sort().pop();

  const passes = plan.passes?.length
    ? [...plan.passes].sort((a, b) => a.order - b.order)
    : STARTING_PASSES;

  /** Every write goes through here, so one cell and the whole vocabulary take the
   *  same path. A plan whose passes are still the defaults carries them along
   *  with the first change, which is what commits them. */
  const write = async (
    key: string,
    body: { passId: string; chapterId?: string; date?: string | null; passes?: StudyPlanPass[] },
  ) => {
    setBusy(key); setError(null);
    try {
      const res = await api.editPlan(plan.id, {
        ownerId: plan.ownerId,
        ...body,
        ...(plan.passes?.length ? {} : { passes: body.passes ?? passes }),
      });
      onPlanChange?.(res.plan);
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(null); }
  };

  // ── Vocabulary ────────────────────────────────────────────────────────
  // A class names its own sweeps: "Formula revision" is a legitimate thing for
  // a class to plan. The names are copied into a student's plan on adopt and
  // never synced, so renaming here does not rewrite what anyone already holds.

  const savePasses = (next: StudyPlanPass[], touched: string) =>
    write(`passes:${touched}`, { passId: touched, passes: next });

  const rename = (p: StudyPlanPass, raw: string) => {
    const name = raw.trim();
    if (!name || name === p.name) return;
    void savePasses(passes.map(x => (x.id === p.id ? { ...x, name } : x)), p.id);
  };

  const addPass = () => {
    const id = newPassId();
    void savePasses([...passes, { id, name: 'New pass', order: passes.length }], id);
  };

  const removePass = (p: StudyPlanPass) => {
    // Renumbered on removal: `order` is what the list sorts by, and a gap would
    // survive into every student's plan that adopts it.
    const next = passes.filter(x => x.id !== p.id).map((x, i) => ({ ...x, order: i }));
    void savePasses(next, p.id);
  };

  /**
   * Bring the plan's subjects in line with a multi-select's new value.
   *
   * One request per ACTUAL change rather than one bulk write: the route adds or
   * removes a single subject, and sending the unchanged ones again would rewrite
   * their chapter dates for nothing. Applied in sequence so the last response is
   * the whole truth — running them together would race on one document.
   */

  const move = (p: StudyPlanPass, by: -1 | 1) => {
    const i = passes.findIndex(x => x.id === p.id);
    const j = i + by;
    if (i < 0 || j < 0 || j >= passes.length) return;
    const next = [...passes];
    [next[i], next[j]] = [next[j], next[i]];
    void savePasses(next.map((x, n) => ({ ...x, order: n })), p.id);
  };

  return (
    <div className="cs cs--plain">

      <AttributeGrid>
        <Attribute label="Name">
          {editing && draft ? (
            <input
              value={draft.name}
              autoFocus
              maxLength={80}
              aria-label="Plan name"
              onChange={e => onDraftChange?.({ ...draft, name: e.target.value })}
            />
          ) : plan.name}
        </Attribute>

        <Attribute label="Visibility">
          {/* One control for all three states, rather than a Share button and an
              unrelated Make-private one: who a plan is for is a property of it,
              and the pair could not express "this class plan is good enough to
              publish" at all. It applies on pick and is deliberately NOT part of
              the draft — publishing has its own rule server-side (`mayPublish`),
              and folding it into Save would make it a side effect of an edit. */}
          {canEdit && canPublish && onSetVisibility ? (
            <span className="cs-attr-inline">
              <select
                value={plan.visibility}
                aria-label="Plan visibility"
                onChange={e => onSetVisibility(e.target.value as StudyPlanVisibility)}
              >
                {visibilityOptions(plan.ownerKind).map(v => (
                  <option key={v} value={v}>{VISIBILITY_LABEL[v]}</option>
                ))}
              </select>
              <span className="cs-muted">
                {VISIBILITY_HINT[plan.visibility]}
                {/* Adopters keep their copy — saying so stops un-sharing reading
                    as a retraction. */}
                {plan.visibility === 'private' && (plan.adoptions ?? 0) > 0
                  && ' Students who already took it keep their own copy.'}
              </span>
            </span>
          ) : (
            <span className="sui-attr-value">{VISIBILITY_LABEL[plan.visibility]}</span>
          )}
        </Attribute>

        <Attribute label="Written for">
          {/* The board and grade, and the reason a subject can be added without
              naming a syllabus: a curriculum is a (board, grade, subject) triple,
              so these two determine the third.
              Editable, with a warning: each subject is pinned to a syllabus of
              THIS pair and its chapter dates ARE that syllabus's placement ids, so
              moving it re-pins every subject and clears those dates. It used to be
              refused outright, which left a plan created at the wrong level
              unrepairable — the dates had to be redone either way. */}
          {editing && draft && boardGradeOptions?.length ? (
            <span className="cs-attr-inline">
              <select
                value={draft.board ?? ''}
                aria-label="Board"
                onChange={e => {
                  const board = e.target.value || undefined;
                  const grades = boardGradeOptions.find(o => o.board === board)?.grades ?? [];
                  // A grade the new board does not teach is not a choice — drop it
                  // rather than leave the pair naming a syllabus that cannot exist.
                  const grade = draft.grade && grades.includes(draft.grade) ? draft.grade : undefined;
                  onDraftChange?.({ ...draft, board, grade });
                }}
              >
                <option value="">Any board</option>
                {boardGradeOptions.map(o => <option key={o.board} value={o.board}>{o.board}</option>)}
              </select>
              <select
                value={draft.grade ?? ''}
                aria-label="Grade"
                disabled={!draft.board}
                onChange={e => onDraftChange?.({ ...draft, grade: e.target.value || undefined })}
              >
                <option value="">Any grade</option>
                {(boardGradeOptions.find(o => o.board === draft.board)?.grades ?? [])
                  .map(g => <option key={g} value={g}>Grade {g}</option>)}
              </select>
              {relevelling && (
                <span className="cs-field-error">
                  Each subject re-pins to the new syllabus and its chapter dates are cleared —
                  the chapter plan has to be made again. Pass dates are kept.
                </span>
              )}
            </span>
          ) : (
            plan.board && plan.grade
              ? `${plan.board} · Grade ${plan.grade}`
              : 'any syllabus — pinned per subject'
          )}
        </Attribute>

        {(canSetDefault || plan.isDefault) && (
        <Attribute label="Default for this level">
          {/* THE plan a student at this board and grade is started on when they
              have chosen nothing — the reason a home screen can show a chart to
              somebody who has never opened the plan page.

              Offered only where it can mean something: a plan from BuzzBrain,
              published, with a level. The server refuses the rest anyway, and a
              checkbox that is going to be rejected is worse than no checkbox. */}
          {editing && draft && canSetDefault ? (
            <span className="cs-attr-inline">
              <label className="cs-check">
                <input
                  type="checkbox"
                  checked={!!draft.isDefault}
                  disabled={!defaultable}
                  onChange={e => onDraftChange?.({ ...draft, isDefault: e.target.checked })}
                />
                Start students at this level on this plan
              </label>
              <span className="cs-muted">
                {!defaultable
                  ? 'Only a published plan from BuzzBrain with a board and grade can be the default.'
                  : draft.isDefault
                    ? 'Whichever plan is the default for this level now stops being it.'
                    : 'One plan per board and grade.'}
              </span>
            </span>
          ) : (
            <span className="sui-attr-value">
              {plan.isDefault
                ? `Yes — ${plan.board ?? 'any board'} · Grade ${plan.grade ?? '—'}`
                : 'No'}
            </span>
          )}
        </Attribute>
        )}

        <Attribute label="Runs until">
          {/* DERIVED, not stored. The last date the plan mentions is what "when is
              this for" actually means, and an authored label beside it could only
              ever drift out of agreement with the dates it describes. */}
          {lastDate ?? 'no dates set'}
        </Attribute>

        <Attribute label="Subjects" span>
          {editing && draft ? (
            /* Chips, not a native multi-select: the native control hides the
               unselected options behind a scroll, needs ctrl-click to add a
               second, and silently drops the lot on a stray click. Here every
               subject is visible and each one is its own decision. */
            <span className="cs-chips">
              {availableSubjects.length === 0 ? (
                <span className="cs-muted">No subjects have a syllabus resolved yet.</span>
              ) : availableSubjects.map(a => {
                const on = draft.subjects.includes(a.subject);
                const covered = (plan.subjects ?? []).some(x => x.subject === a.subject);
                const dated = covered && Object.values(
                  (plan.subjects ?? []).find(x => x.subject === a.subject)?.chapterTargets ?? {},
                ).some(t => t.length > 0);
                const label = subjectName?.(a.subject) ?? a.subject;
                /**
                 * A subject with no syllabus cannot JOIN a plan — but one already
                 * in it must still come out, or a plan that predates this rule
                 * could never be repaired.
                 *
                 * Shown rather than hidden: the class teaches it, and hiding it
                 * would read as the subject being missing rather than its syllabus.
                 */
                const noSyllabus = !a.curriculumId && !on;
                return (
                  <button
                    key={a.subject}
                    type="button"
                    className={`cs-chip${on ? ' cs-chip--on' : ''}${noSyllabus ? ' cs-chip--nosyllabus' : ''}`}
                    aria-pressed={on}
                    aria-disabled={noSyllabus}
                    // Removing a subject takes its chapter dates with it — they are
                    // that syllabus's placement ids and mean nothing without it. Said
                    // only when there are actually dates to lose.
                    title={
                      noSyllabus
                        ? `There is no curriculum for ${label}, so it cannot be added to a plan.`
                        : on && dated
                          ? `Removing ${label} removes its chapter dates too`
                          : undefined
                    }
                    onClick={() => {
                      if (noSyllabus) {
                        setSubjectError(
                          `There is no curriculum for ${label}, so it cannot be added to a plan.`,
                        );
                        return;
                      }
                      setSubjectError(null);
                      onDraftChange?.({
                        ...draft,
                        subjects: on
                          ? draft.subjects.filter(x => x !== a.subject)
                          : [...draft.subjects, a.subject],
                      });
                    }}
                  >
                    {label}
                  </button>
                );
              })}
              {/* Under the chips, not on the chip: a title attribute is invisible
                  on touch, where this refusal is most likely to be met. */}
              {subjectError && <span className="cs-field-error">{subjectError}</span>}
            </span>
          ) : (
            (plan.subjects ?? []).length === 0
              ? '—'
              : (plan.subjects ?? []).map(x => subjectName?.(x.subject) ?? x.subject).join(', ')
          )}
        </Attribute>

        <Attribute label="Taken by">
          {/* Honest about being a weak signal: it counts what was taken, never
              what worked. */}
          {plan.adoptions ? `${plan.adoptions} student${plan.adoptions === 1 ? '' : 's'}` : 'nobody yet'}
        </Attribute>

        <Attribute label="Description" span>
          {editing && draft ? (
            <textarea
              value={draft.description}
              maxLength={500}
              rows={2}
              placeholder="Who it is for and what it assumes — how much time a week, and what it skips."
              aria-label="Plan description"
              onChange={e => onDraftChange?.({ ...draft, description: e.target.value })}
            />
          ) : (plan.description || '—')}
        </Attribute>
      </AttributeGrid>

        <h4 className="cs-section-title">
          Passes and dates
          {/* Says what Save does NOT cover. These write one cell per change so each
              history entry is exactly what moved, which is what an adopter's
              "the plan you took has changed" prompt renders. */}
          {editing && <span className="cs-meta cs-inline-note">saved as you change them</span>}
        </h4>
        {/* One pass per LINE. They are an ordered sequence — first read, then a
            revision, then another — and a wrapping row of cards showed that order
            as a reading direction nobody follows. The date sits beside its name,
            and the controls that move it sit at the end of its own row. */}
        <div className="cs-pass-rows">
          {passes.map((p, i) => (
            <div key={p.id} className="cs-pass-row">
              <span className="cs-pass-name">
                {/* No per-pass edit icon: the page is either in edit mode or it is
                    not, and a pencil on every row asked the reader to opt in twice. */}
                {canEdit && editing ? (
                  <input
                    className="cs-plan-rename"
                    defaultValue={p.name}
                    key={`pass-${p.id}-${p.name}`}
                    aria-label={`Name of ${p.name}`}
                    onBlur={e => rename(p, e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                  />
                ) : (
                  <span className="cs-name-plain">{p.name}</span>
                )}
              </span>

              <span className="cs-pass-date">
                {canEdit && editing ? (
                <DatePicker
                  value={plan.passDates?.[p.id] ?? ''}
                  disabled={busy === p.id}
                  ariaLabel={`Date every subject finishes ${p.name}`}
                  placeholder="Set date"
                  // Saved on change, one cell at a time — the history entry is
                  // then exactly what changed.
                  onChange={iso => void write(p.id, { passId: p.id, date: iso || null })}
                />
                ) : (
                  <span className={formatPlanDate(plan.passDates?.[p.id]) ? '' : 'cs-muted'}>
                    {formatPlanDate(plan.passDates?.[p.id]) ?? NO_DATE}
                  </span>
                )}
              </span>

              {canEdit && editing && (
                <span className="cs-row-actions cs-pass-actions">
                  <button
                    type="button" className="cs-icon"
                    aria-label={`Move ${p.name} earlier`} title="Move earlier"
                    disabled={i === 0}
                    onClick={() => move(p, -1)}
                  >↑</button>
                  <button
                    type="button" className="cs-icon"
                    aria-label={`Move ${p.name} later`} title="Move later"
                    disabled={i === passes.length - 1}
                    onClick={() => move(p, 1)}
                  >↓</button>
                  <button
                    type="button" className="cs-icon cs-icon--danger"
                    aria-label={`Remove ${p.name}`}
                    // Says what it costs: students who already adopted keep their
                    // copy, so this is not a retraction.
                    title={`Remove ${p.name}. Students who already took this plan keep theirs.`}
                    disabled={passes.length === 1}
                    onClick={() => removePass(p)}
                  >×</button>
                </span>
              )}
            </div>
          ))}
          {canEdit && editing && (
            <button type="button" className="cs-link cs-plan-add" onClick={addPass}>
              + Add a pass
            </button>
          )}
        </div>

      {error && <p className="cs-error">{error}</p>}

    </div>
  );
}
